import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n/TranslationProvider";

import forIn from "lodash/forIn";

import { Row, Col, Form } from "react-bootstrap";
import Sallie from "../UIComponents/Sallie";
import ListSelector from "../UIComponents/ListSelector";
import useListSelector from "../../lib/useListSelector";
import OnboardingStepHeader from "./OnboardingStepHeader";
import SubmitButton from "../UIComponents/SubmitButton";

import {
  postTrackedFactors,
  postWorstSymptomsFactors,
} from "../../api/TBIRequests";
import customizeMessages from "../../lib/customizeMessages";

const SelectTopSymptoms = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [noSymptom, setNoSymptom] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { forwardData } = location.state || { forwardData: undefined };

  const [returnedData, setReturnedData] = useState([]);

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  if (!patient) {
    history.push("/oops");
  }

  const createItemList = () => {
    const items = [];
    forIn(forwardData.selectedItems, (value) => {
      value.forEach((item) => items.push(item));
    });
    const itemList = [{ category: "data", items, maxSelectAmount: "3" }];
    return itemList;
  };

  const worstSymptomsForSending = () => {
    return forwardData.processedData
      .filter((item) => Object.values(selectedData).flat().includes(item.factor))
      .map((item) => ({
        factor: item.factor,
        category: item.category,
        subcategory: item.subcategory,
      }));
  };

  const allTrackingSymptomsForSending = () => {
    return forwardData.processedData.map((item) => ({
      factor: item.factor,
      category: item.category,
      subcategory: item.subcategory,
    }));
  };

  useEffect(() => {
    if (!forwardData) {
    } else {
      const itemList = createItemList();
      initialize(itemList);
      setReturnedData(itemList);
    }
    setLoading(false);
    location.state?.fromPatientControl
      ? setOnboardingPercent(80)
      : setOnboardingPercent(50);
  }, [forwardData]);

  const handleSubmit = () => {
    setSubmitting(true);
    const worstSymptoms = worstSymptomsForSending();
    const allTrackingSymptoms = allTrackingSymptomsForSending();
    const selectedCount = Object.values(selectedData).flat().length;

    if (selectedCount < 3) {
      setErrorMessage(true);
      setSubmitting(false);
      return;
    }

    if (allTrackingSymptoms.length === 0) {
      setSubmitting(false);
      setNoSymptom(true);
      return;
    }

    postWorstSymptomsFactors({ patientId: patient.patientId, data: worstSymptoms })
      .catch((error) => {
        console.error("An error occurred while posting tracked factors:", error);
        history.push("/oops");
      });

    postTrackedFactors({
      patientId: patient.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
      data: allTrackingSymptoms,
    })
      .then(() => {
        location.state?.fromPatientDashboard
          ? history.push("/", { fromPatientDashboard: true })
          : history.push("/selectTrigger", {
              fromPatientControl: location.state?.fromPatientControl,
            });
      })
      .catch((error) => {
        console.error("An error occurred while posting tracked factors:", error);
        history.push("/oops");
      });
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("topsymptoms.header"),
      tbiPatient: t("topsymptoms.header"),
      caregiver: t("topsymptoms.header"),
    },
    sallieText: {
      Stroke: t("topsymptoms.sallie"),
      tbiPatient: t("topsymptoms.sallie"),
      caregiver: t("topsymptoms.sallie"),
    },
    noSymptomsText: {
      Stroke: t("topsymptoms.error.nosymptom"),
      tbiPatient: t("topsymptoms.error.nosymptom"),
      caregiver: t("topsymptoms.error.nosymptom"),
    },
    selectSymptomsCategory: {
      Stroke: t("topsymptoms.body"),
      tbiPatient: t("topsymptoms.body"),
      caregiver: t("topsymptoms.body"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />
          {returnedData.map((itemList, i) => (
            <ListSelector
              key={itemList.category}
              category={itemList.category}
              message={getMessage("selectSymptomsCategory")}
              list={itemList.items}
              index={i}
              selectedList={selectedData[itemList.category]}
              toggleFromSelectedList={toggleFromSelectedList}
              selectOne={switchSingleSelection}
              maxSelectAmount={itemList.maxSelectAmount}
              noOther
              noSuffix
            />
          ))}
          <Row>
            <Col>
              {errorMessage && (
                <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                  {t("topsymptoms.error.select3")}
                </Form.Control.Feedback>
              )}
              {noSymptom && (
                <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                  {getMessage("noSymptomsText")}
                </Form.Control.Feedback>
              )}
            </Col>
            <Col>
              {location.state?.fromPatientDashboard ? (
                <SubmitButton disabled={submitting} type="button" onClick={handleSubmit}>
                  {t("onboarding.saveAndGoToDashboard")} &gt;
                </SubmitButton>
              ) : (
                <SubmitButton disabled={submitting} type="button" onClick={handleSubmit}>
                  {t("onboarding.saveContinue")} &gt;
                </SubmitButton>
              )}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SelectTopSymptoms;